import React, { useEffect, useRef } from 'react';

export const FlowingWave: React.FC<{ tone?: "faint" | "ink" }> = ({ tone = "faint" }) => {
  // "ink": darker and slightly heavier, to sit with bold outlined content (mokachika.com's tiles, 5 October 2026)
  const A = tone === "ink" ? 2.3 : 1, Wt = tone === "ink" ? 1.7 : 1;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let time = 0;
    let displayWidth = 0;
    const displayHeight = 160; // Increased for padding

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      displayWidth = rect.width || window.innerWidth;

      canvas.width = Math.round(displayWidth * dpr);
      canvas.height = Math.round(displayHeight * dpr);

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const speed = 0.01;

    const getWaveY = (progress: number) => {
      // Responsive amplitude - smaller on mobile
      const isMobile = window.innerWidth < 768; // md breakpoint
      const amplitude = isMobile ? 20 : 35; // Smaller wave on mobile
      const drift = isMobile ? 5 : 10; // Less drift on mobile too

      const base = Math.sin((progress * Math.PI * 1.5) - time * 0.5) * amplitude;
      const detail1 = Math.sin((progress * Math.PI * 3) + time * 0.8) * (amplitude * 0.2);
      const detail2 = Math.sin((progress * Math.PI * 5) - time * 1.2) * (amplitude * 0.1);
      const driftAmount = Math.sin(time * 0.3) * drift;
      // Keep wave centered with padding top and bottom
      return displayHeight / 2 + base + detail1 + detail2 + driftAmount;
    };

    const drawWave = () => {
      // Ensure we have valid dimensions
      if (!displayWidth || !displayHeight) {
        resizeCanvas();
        return;
      }

      ctx.clearRect(0, 0, displayWidth, displayHeight);

      // Draw wave with explicit context settings
      ctx.save();
      ctx.strokeStyle = '#1C1917';
      ctx.lineWidth = 0.8 * Wt;
      ctx.lineCap = 'round';
      ctx.globalAlpha = Math.min(1, 0.25 * A);

      const points = 200;

      // Generate smooth cubic bezier curve through points
      const wavePoints: {x: number, y: number}[] = [];

      for (let i = 0; i <= points; i++) {
        const progress = i / points;
        const x = progress * displayWidth;
        const y = getWaveY(progress);
        // Validate points
        if (!isNaN(x) && !isNaN(y) && isFinite(x) && isFinite(y)) {
          wavePoints.push({x, y});
        }
      }

      // Draw smooth curve through all points
      if (wavePoints.length > 1) {
        ctx.beginPath();
        ctx.moveTo(wavePoints[0].x, wavePoints[0].y);

        for (let i = 1; i < wavePoints.length; i++) {
          ctx.lineTo(wavePoints[i].x, wavePoints[i].y);
        }

        ctx.stroke();
      }
      ctx.restore();

      // Draw boat - responsive sizing
      const isMobile = window.innerWidth < 768;
      const boatScale = isMobile ? 0.7 : 1.0; // Smaller boat on mobile
      const boatX = displayWidth * 0.65; // Position near end of "mokachika"
      const boatProgress = 0.65;
      const boatY = getWaveY(boatProgress) - (8 * boatScale); // Bottom touches wave

      // Calculate tilt angle from wave slope
      const delta = 0.01;
      const y1 = getWaveY(boatProgress - delta);
      const y2 = getWaveY(boatProgress + delta);
      const angle = Math.atan2(y2 - y1, displayWidth * delta * 2);

      // Draw boat with separate context saves for each part
      ctx.save();
      ctx.translate(boatX, boatY);
      ctx.rotate(angle);
      ctx.scale(boatScale, boatScale); // Apply scale

      // Draw boat hull
      ctx.globalAlpha = Math.min(1, 0.3 * A);
      ctx.fillStyle = '#1C1917';
      ctx.beginPath();
      ctx.moveTo(-12, 0);   // Left
      ctx.lineTo(12, 0);    // Right
      ctx.lineTo(8, 8);     // Bottom right
      ctx.lineTo(-8, 8);    // Bottom left
      ctx.closePath();
      ctx.fill();

      // Draw mast
      ctx.globalAlpha = Math.min(1, 0.4 * A);  // Reset alpha for mast
      ctx.strokeStyle = '#1C1917';
      ctx.lineWidth = 1 * Wt;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -15);
      ctx.stroke();

      // Draw sail
      ctx.globalAlpha = Math.min(1, 0.2 * A);  // Explicit alpha for sail
      ctx.fillStyle = '#1C1917';
      ctx.beginPath();
      ctx.moveTo(0, -15);
      ctx.lineTo(8, -8);
      ctx.lineTo(0, -2);
      ctx.closePath();
      ctx.fill();

      ctx.restore();

      time += speed;
      animationFrameId = requestAnimationFrame(drawWave);
    };

    drawWave();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="w-full mb-md md:mb-xl overflow-hidden">
      <canvas ref={canvasRef} className="w-full" style={{ height: '160px' }} />
    </div>
  );
};
